/**
 * Customize SRT functionality. Methods used to extend SRT directly should be exported, but additional project callbacks within those calls may be inner methods. See [`App.customize`]{@link module:core/app.customize} for more information on extending SRT.
 *
 * @module project/customize
 */

// Import everything from autoload
import './autoload/template-partials.js'; import './autoload/touchswipe.min.js';

// import necessary app functionality
//import * as Util from 'srt-core/util'
import * as App from "srt-core/app";
import * as View from "srt-core/view";
import * as Util from "srt-core/util";

// import project-specific functionality

/**
 * Project default settings.
 *
 * See [project content settings]{@link module:project/config.app_settings} for more information on configuration.
 *
 * @type {object}
 *
 * @since 4.2.2
 */
export const config = {
  progress_bar_count: 0,
  progress: 0,
};

/**
 * Customize config defaults.
 *
 * - [Update app config]{@link module:core/app.updateConfig} with project defaults.
 *
 * @method
 *
 * @since 4.2.2
 *
 * @see [`App.updateConfig`]{@link module:core/app.updateConfig}
 */
export const configInit = () => {
  // add project config defaults
  App.updateConfig(config);
};

/**
 * Initialize project functionality.
 *
 * - [Set dynamic reference strings]{@link module:project/customize~setDynamicRefs}.
 *
 * @method
 *
 * @since 4.1.0
 *
 * @see [`setDynamicRefs`]{@link module:project/customize~setDynamicRefs}
 */
export const init = () => {
  // if direct-to-retailer is enabled, set start to overwhelmed path
  if (App.settings.direct_to_retailer.enabled) App.updateSettings({start: 'form_factor'});
  // setup custom dynamic references
  setDynamicRefs();
}

export const addEventListeners = () => {
  // product modal event listeners
  View.container.on('click', '.js-productModalButton', (event) => {
    event.preventDefault();
    let button = event.currentTarget;
    const href = button.getAttribute('href');
    productModal(href);
  });
};

/**
 * Customize current view content.
 *
 * @method
 *
 * @param {object} view - Current view content.
 *
 * @see [`setProgress`]{@link module:project/customize~setProgress}
 * @see [`setProductContent`]{@link module:project/customize~setProductContent}
 */
export const setCurrentContent = (view) => {
  // set progress percent
  setProgress(view);
  // set product content
  setProductContent(view);
};

/**
 * Custom [post-render view actions]{@link module:core/view.postRenderViewActions}.
 *
 * - [Build star rating classes]{@link module:project/customize.applyStarClasses}, by looping through each star and giving it either a full or half star class.
 *
 * @method
 *
 * @since 4.1.0
 *
 * @see [`applyStarClasses`]{@link module:project/customize.applyStarClasses}
 */
export const postRenderViewActions = () => {
  // render rating stars
  applyStarClasses();
};

/**
 * Customize product spec labels.
 *
 * - `price` - Product price attribute is formatted based on `price.format_number` settings in filter schema.
 *     - A special case exists for `es-ar` lang builds, in which numbers are custom formatted to LATAM standards, e.g.: `999,99`, `1999,99`, `10 999,99`.
 * - `display` - Display value is formatted based on `display.format_number` settings in filter schema, and inserted into a template string provided by the translated JSON content. Note that in many cases, the default formatted unit is replaced with the inches symbol `"` for consistency throughout the app.
 * - `ram` - Memory attribute is formatted into either GB or TB, and inserted into a template string provided by translated JSON content.
 * - `storage_amount` - Each drive type is formatted into either GB or TB, inserted into a relevant template string provided by translated JSON content, and joined together with a ` + ` for the final storage amount label.
 *
 * @method
 *
 * @param {object} formatFunctions - Object containing custom formatting functions for product attributes.
 */
export const setProductLabels = (formatFunctions) => {
  // labels content shortcut
  let labels = App.settings.labels.attributes;
  // filter attribute schema shortcut
  let schema = App.settings.filter.schema;
  // define custom label functions
  let customFunctions = {
    price: (product) => {
      let label = Util.formatNumber(product.attributes.price, schema.price.format_number.options);
      if (App.settings.lang === 'es-ar') {
        label = product.attributes.price > 9999 ? label.replace(/\./g, ' ') : label.replace('.', '');
      }
      return label;
    },
    display: (product) => {
      let label = `${Util.formatNumber(product.attributes.display, schema.display.format_number.options)}`;
      if (['cs', 'de', 'en', 'en-gb', 'es', 'es-ar', 'he', 'it', 'ja', 'ro'].includes(App.settings.lang)) label = label.replace(' in', '"&lrm;');
      if (App.settings.lang === 'fr') label = label.replace('po', '"');
      if (App.settings.lang === 'fr-ca') label = label.replace(' po', '"');
      if (App.settings.lang === 'hu') label = label.replace(' hüvelyk', '"');
      if (App.settings.lang === 'pt-br') label = label.replace(' pol.', '"');
      if (App.settings.lang === 'ru') label = label.replace(' дюйм.', '"');
      if (App.settings.lang === 'sk') label = label.replace('in', 'palcov');
      if (App.settings.lang === 'sv') label = label.replace(' tum', '"');
      if (App.settings.lang === 'zh-Hans') label = label.replace(`${product.attributes.display}`, `${product.attributes.display} `);
      label = labels.display.template.replace('[size]', label);
      return label;
    },
    ram: (product) => Util.formatBytes(product.attributes.ram, labels.ram.template),
    // generate storage amount labels
    storage_amount: (product) => {
      // filter storage type values to only those included in product
      let driveValues = schema.storage_type.values.filter(({value}) => product.attributes.storage_type.includes(value));
      // return formatted bytes labels
      return driveValues.map(({value}) => Util.formatBytes(product.attributes[value], labels.storage.template[value])).join(' + ');
    },
  };

  // add custom functions for format functions object
  Object.assign(formatFunctions, customFunctions);
};

/**
 * Customize "view all" content.
 *
 * @method
 *
 * @param {object} viewAllContent - Page settings and content for "view all" page.
 *
 * @see Extends:
 * @see [`App.viewAllInit`]{@link module:core/app.viewAllInit}
 */
export const viewAllInit = (viewAllContent) => {
  Util.merge(viewAllContent, {
    settings: {
      get_products: 12,
    },
    ref: {
      content: ['specs', 'recommendation'],
    },
  });
};

/**
 * Set dynamic reference tags and their replacement value. See the `args.string` param in [`App.transformContent`]{@link module:core/app.transformContent} for more information on using dynamic reference tags.
 *
 * - `[products_remaining]` - `<span class="js-products-remaining"></span>`
 *
 * @since 4.2.0
 *
 * @see Used by:
 * @see [**`init`**]{@link module:project/customize.init}
 */
const setDynamicRefs = () => {
  App.updateSettings({
    dynamic_refs: {
      products_remaining: '<span class="js-products-remaining"></span>',
    },
  });
};

/**
 * Add classes to product stars that correspond to the product's rating (if provided).
 *
 * - Runs a loop for each integer in the provided rating, decrementing the rating on each loop.
 * - Adds a "full" star class if remaining rating is greater than one.
 * - Adds a "half" star class if less than one.
 *
 * @since 4.1.0
 *
 * @see Used by:
 * @see [**`postRenderViewActions`**]{@link module:project/customize.postRenderViewActions}
 */
const applyStarClasses = () => {
  if (View.container.find('.ct-products').length) {
    const ratings = View.container.find('.js-starRating');
    ratings.each(function() {
      let stars = $(this).find('.p-star');
      let rating = parseFloat(this.getAttribute('data-rating'));
      for (let i = 0; rating > 0; i++, rating--) {
        if (rating >= 1) {
          stars.eq(i).addClass('p-star-full');
        } else {
          stars.eq(i).addClass('p-star-half');
        }
      }
    });
  }
};

/**
 * Set current progress bar percent. Uses relationship between `progress_bar` page setting and `progress_bar_count` app setting, and stores value to `progress` setting.
 *
 * @param {object} view - Current view content.
 *
 * @see Used by:
 * @see [**`setCurrentContent`**]{@link module:project/customize.setCurrentContent}
 */
const setProgress = (view) => {
  if (!view.settings.progress_bar) return;
  view.settings.progress = view.settings.progress_bar / (view.settings.progress_bar_count + 1) * 100;
};

/**
 * Inject additional product content, including processor generation badge names, into product content lists.
 *
 * The following processing is completed for each product contained in view:
 *
 * #### Set processor badge name
 * Get the current processor name and generation (if applicable) and generate a string name based on those values. This string is used to reference a badge image path in app content, e.g. `settings.images.i7-11`.
 *
 * @param {object} view - Current view content.
 *
 * @see Used by:
 * @see [**`setCurrentContent`**]{@link module:project/customize.setCurrentContent}
 */
const setProductContent = (view) => {
  // do nothing if no products are present
  if (!view.products) return;
  // add data to products
  for (let pi = 0; pi < view.products.length; pi++) {
    const product = view.products[pi];
    // get badge name
    const processor = product.attributes.processor;
    const isXeon = processor && processor.includes('xeon');
    let imgName = isXeon ? 'xeon' : product.attributes.processor;
    if (!isXeon) {
      const gen = product.attributes.processor_gen;
      if (gen === 10 || gen === 11) imgName += `-${gen}`;
    }
    product.badge = imgName;
  }
};

/**
 * View product in a modal popup instead of linking offsite, when `modal_product` [view setting]{@link module:core/config.view} is enabled.
 *
 * @since 4.2.2
 *
 * @param {string} url - The product URL to view.
 */
const productModal = (url = '') => {
  // do nothing if modal product not enabled
  if (!App.settings.view.modal_product) return;
  // get product page modal
  let content = $('.js-productModal');
  // set modal iframe
  content.html(`<iframe src="${url}"></iframe>`);
  // toggle popup
  View.togglePopup(true, '.js-productModal');
};
